import React, { FC, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import { toastOptions } from "config/constants";
import { useAppDispatch, useAppSelector } from "config/hooks";
import {
  cancelLoginCode,
  resendLoginCode,
  verifyLoginCode,
} from "../../reducers/auth";

// Second sign-in step (RM-3849): owl emailed a 6-digit code because this
// browser isn't remembered. "Remember this device" is on by default so the
// common case is one code per browser per 30 days.
const LoginCodeStep: FC = () => {
  const dispatch = useAppDispatch();
  const emailHint = useAppSelector((state) => state.auth.loginCode?.emailHint);
  const errors = useAppSelector((state) => state.auth.errors) ?? [];
  const isLoading = useAppSelector((state) => state.auth.isLoading);
  const [code, setCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(true);
  const [isResending, setIsResending] = useState(false);

  const digits = code.replace(/\D/g, "");

  const submit = () => {
    if (digits.length !== 6 || isLoading) return;
    dispatch(verifyLoginCode({ code: digits, rememberDevice }) as any);
  };

  const resend = async () => {
    setIsResending(true);
    const error = await (dispatch(
      resendLoginCode() as any,
    ) as unknown as Promise<string | null>);
    setIsResending(false);
    if (error) {
      toast.error(error, toastOptions);
    } else {
      setCode("");
      toast.success("We sent you a new code", toastOptions);
    }
  };

  return (
    <>
      <div className="modal-body">
        <div className="app-logo mx-auto mb-3" />
        <h4 className="text-center mb-2">Check your email</h4>
        <p className="text-muted text-center mb-3">
          We sent a 6-digit code to <strong>{emailHint}</strong>. It expires in
          10 minutes.
        </p>
        <TextField
          value={code}
          onChange={(e) =>
            setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
          autoFocus
          fullWidth
          name="code"
          id="login-code"
          placeholder="123456"
          slotProps={{
            htmlInput: {
              "aria-label": "Sign-in code",
              inputMode: "numeric",
              autoComplete: "one-time-code",
              style: {
                textAlign: "center",
                fontSize: "1.5rem",
                letterSpacing: "0.4em",
              },
            },
          }}
        />
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
            />
          }
          label="Remember this device for 30 days"
        />
        <div className="text-center mt-2">
          {errors.map((e: any, i: number) => (
            <div key={`e${i}`} className="text-danger">
              {e}
            </div>
          ))}
        </div>
      </div>
      <div className="modal-footer clearfix">
        <Box
          sx={{ display: "flex", width: "100%", alignItems: "center", gap: 1 }}
        >
          <Button variant="text" disabled={isResending} onClick={resend}>
            Resend code
          </Button>
          <Button
            variant="text"
            onClick={() => dispatch(cancelLoginCode() as any)}
          >
            Use a different account
          </Button>
          <Button
            variant="contained"
            size="large"
            sx={{ ml: "auto" }}
            disabled={isLoading || digits.length !== 6}
            onClick={submit}
          >
            Verify
          </Button>
        </Box>
      </div>
    </>
  );
};

export default LoginCodeStep;
